import { Check, Edit } from "@mui/icons-material";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { Dialog, Button, ClickAwayListener, Tooltip } from "@mui/material";
import cx from "classnames";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import { ReactComponent as EditIcon } from "assets/pen.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import ShotContext from "components/Shot/ShotContext";
import {
  SERVICES,
  STATUSES,
  ViewModes,
  WINDOW_EVENTS,
} from "constants/constants";
import { CutoutInProgress } from "models/Cutout";
import { LayerCompact } from "models/Layer";
import { renameLayer } from "services/LayerService";
import { useClickHandler } from "utils/hooks";

import styles from "./Layers.module.scss";

const OVERLAY_BY_SERVICE = {
  [SERVICES.ROTOSCOPING]: "Inferencing...",
  [SERVICES.RETIME]: "Retiming...",
  [SERVICES.DEPTH_MAP]: "Generating depth map...",
  [SERVICES.TRACKING]: "Generating camera track...",
  [SERVICES.SMART_VECTORS]: "Generating Motion Vectors...",
};

interface LayerProps {
  layer: LayerCompact;
  isAnnotating: boolean;
  layerId: string | undefined;
  selectedLayer: LayerCompact | null;
  setLayerAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  setSelectedLayer: React.Dispatch<React.SetStateAction<LayerCompact | null>>;
  setCutoutInProgress: React.Dispatch<
    React.SetStateAction<CutoutInProgress | null>
  >;
  layersViewMode: ViewModes;
  showRenameLayerModal?: boolean;
  setShowRenameLayerModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setLayers: React.Dispatch<React.SetStateAction<LayerCompact[] | undefined>>;
  layers?: LayerCompact[];
  layerInputEl?: React.MutableRefObject<HTMLInputElement | null>;
  handleSingleClick: () => void;
  setShowLayerDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Set by parents that render a `cascadeHeader` above this card. When true,
   * the in-card `layerDetails` block (name display + List-mode status hints)
   * AND the hover-revealed delete/edit controls at the bottom-right of the
   * thumbnail are suppressed — they're handled by the header instead.
   */
  wrappedByHeader?: boolean;
}
const Layer: React.FC<LayerProps> = ({
  layer,
  isAnnotating,
  layerId,
  selectedLayer,
  setSelectedLayer,
  setCutoutInProgress,
  setLayerAnchorEl,
  layersViewMode,
  showRenameLayerModal,
  setShowRenameLayerModal,
  setLayers,
  layers,
  layerInputEl,
  handleSingleClick,
  setShowLayerDeleteModal,
  wrappedByHeader,
}) => {
  const { service, shotCutouts } = useContext(ShotContext);
  const [failedLayer, setFailedLayer] = useState<string | null>(null);

  const [hovered, setHovered] = useState<boolean>(false);

  /** Right-hand layer thumbnail: keep rotoscoping-style ROI cover until MV completes. */
  const isSmartVectorsWithPreview =
    service === SERVICES.SMART_VECTORS &&
    layer.latest_inference_cutout_status === STATUSES.COMPLETED &&
    (layer.smart_vectors_run_type === "full_shot" ||
      layer.smart_vectors_run_type === "roi");

  /** When layer API omits cover during MV inferencing, use cutout preview/mask URLs. */
  const smartVectorsRoiThumbUrl = useMemo(() => {
    if (service !== SERVICES.SMART_VECTORS) return null;
    const forLayer = (shotCutouts || []).filter((c) => c.layer_id === layer.id);
    if (!forLayer.length) return null;
    const sorted = [...forLayer].sort((a, b) => b.id - a.id);
    const found = sorted.find((c) => c.preview_image_url || c.image_url);
    if (found) return found.preview_image_url || found.image_url;
    return null;
  }, [service, shotCutouts, layer.id]);

  /** Last thumbnail shown before MV inferencing (cover or ROI preview). Ignored while IN_PROGRESS so newest inference cutout previews do not replace it. */
  const lastSvAnnotationThumbRef = useRef<string | null>(null);
  const combinedIdleThumb =
    layer.cover_image_url || smartVectorsRoiThumbUrl || null;
  useEffect(() => {
    if (service !== SERVICES.SMART_VECTORS) return;
    if (layer.latest_inference_cutout_status !== STATUSES.IN_PROGRESS) {
      if (combinedIdleThumb) {
        lastSvAnnotationThumbRef.current = combinedIdleThumb;
      }
    }
  }, [
    service,
    layer.id,
    layer.latest_inference_cutout_status,
    combinedIdleThumb,
  ]);

  const layerThumbUrl = useMemo(() => {
    if (
      service === SERVICES.SMART_VECTORS &&
      layer.latest_inference_cutout_status === STATUSES.IN_PROGRESS
    ) {
      return layer.cover_image_url || lastSvAnnotationThumbRef.current || "";
    }
    return layer.cover_image_url || smartVectorsRoiThumbUrl || "";
  }, [
    service,
    layer.cover_image_url,
    layer.latest_inference_cutout_status,
    smartVectorsRoiThumbUrl,
  ]);

  const handleClick = useClickHandler(handleSingleClick, () => {
    if (layer.is_autopilot_layer) return;
    setSelectedLayer(layer);
    setShowRenameLayerModal?.(true);
  });

  const getStatusClassNames = (status) => {
    const styleNames = [styles.layerContainer];
    styleNames.push(
      layersViewMode === ViewModes.Preview
        ? styles.previewLayerContainer
        : styles.listLayerContainer,
    );
    if (
      !(
        service === SERVICES.TRACKING &&
        shotCutouts.filter(
          (c) =>
            c.layer_id === layer.id &&
            (!c.inference || c.inference.status === null),
        ).length > 0
      )
    ) {
      switch (status) {
        case STATUSES.FAILED:
          styleNames.push(styles.inferenceFailed);
          break;
        case STATUSES.IN_PROGRESS:
          styleNames.push(
            styles.inferencing,
            layersViewMode === ViewModes.Preview
              ? styles.previewInferencing
              : styles.listInferencing,
          );
          if (
            selectedLayer?.id === layer.id ||
            (layerId && +layerId) === layer.id
          )
            styleNames.push(styles.selectedLayerInferencing);
          break;
        case STATUSES.COMPLETED:
          styleNames.push(styles.inferenceSuccess);
          break;
        // no default
      }
    }
    if (
      (layerId && +layerId === layer.id && !isAnnotating) ||
      selectedLayer?.id === layer.id
    )
      styleNames.push(styles.selectedLayer);

    return styleNames;
  };

  const layerNameIsValid = (name: string) => {
    const LAYER_NAME_PATTERN = /^(?=.{3,}$)(?![\s])[a-zA-Z0-9\s\-_]+$/;
    if (!name.length) return false;
    if (!LAYER_NAME_PATTERN.test(name)) {
      setFailedLayer(name);
      return false;
    }
    return true;
  };

  return (
    <div
      key={layer.id}
      className={cx(getStatusClassNames(layer.latest_inference_cutout_status))}
      onClick={handleClick}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (layer.is_autopilot_layer) return;
        setLayerAnchorEl(e.currentTarget);
        setSelectedLayer(layer);
      }}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      {layer.deleted && <div className={styles.deletedLabel}>Deleted</div>}

      {layer.has_viewed_latest_inference === false &&
        layer.latest_inference_cutout_status === STATUSES.COMPLETED && (
          <div className={styles.checkMarkCon}>
            <Check sx={{ fontSize: 12 }} className={styles.checkMark} />
          </div>
        )}

      {hovered && !wrappedByHeader && (
        <div className={styles.hoveredCon}>
          {service === SERVICES.TRACKING && (
            <div
              className={styles.deleteButton}
              onClick={(e) => {
                e.stopPropagation();
                const layerCutouts = shotCutouts.filter(
                  (c) => c.layer_id === layer.id,
                );
                if (layerCutouts.length) {
                  const { frame } = layerCutouts[0];
                  setSelectedLayer(layer);
                  window.dispatchEvent(
                    new CustomEvent(WINDOW_EVENTS.ANNOTATE, {
                      detail: { frame },
                    }),
                  );
                }
              }}
            >
              <EditIcon style={{ width: 14, height: 14 }} />
            </div>
          )}
          {!layer.is_autopilot_layer && (
            <div
              className={styles.deleteButton}
              onClick={(e) => {
                e.stopPropagation();
                setLayerAnchorEl(null);
                setSelectedLayer(layer);
                setShowLayerDeleteModal(true);
              }}
            >
              <DeleteIcon />
            </div>
          )}
        </div>
      )}

      {isSmartVectorsWithPreview ? (
        <div
          className={cx(
            styles.layerThumbnail,
            layersViewMode === ViewModes.Preview
              ? styles.previewThumbnail
              : styles.listThumbnail,
          )}
          style={{
            backgroundColor: layer.color,
            padding: "0px",
          }}
        >
          {layersViewMode === ViewModes.Preview && (
            <>
              {layer.latest_inference_cutout_status === STATUSES.FAILED && (
                <div className={styles.statusOverlay}>
                  Error occurred while inferencing
                </div>
              )}
              {layer.latest_inference_cutout_status ===
                STATUSES.IN_PROGRESS && (
                <div className={styles.statusOverlay}>
                  {OVERLAY_BY_SERVICE[service]}
                </div>
              )}
              {layer.is_processing_masks && (
                <div className={styles.statusOverlay}>
                  Processing...
                  <div className={styles.loader}>
                    <div className={styles.pipe} />
                    <div className={styles.dashes} />
                  </div>
                </div>
              )}
            </>
          )}
          {layerThumbUrl ? (
            <img
              alt=""
              className={cx(styles.thumbnailImage, styles.thumbnailImageFill)}
              src={layerThumbUrl}
            />
          ) : (
            <AspectRatioIcon
              className={
                layersViewMode === ViewModes.Preview
                  ? styles.largeIcon
                  : styles.smallIcon
              }
            />
          )}
        </div>
      ) : (
        <div
          className={cx(
            styles.layerThumbnail,
            layersViewMode === ViewModes.Preview
              ? styles.previewThumbnail
              : styles.listThumbnail,
          )}
          style={{
            backgroundColor: layer.color,
            padding: service === SERVICES.ROTOSCOPING ? "imherit" : "0px",
          }}
        >
          {layersViewMode === ViewModes.Preview && (
            <>
              {layer.latest_inference_cutout_status === STATUSES.FAILED && (
                <div className={styles.statusOverlay}>
                  Error occurred while inferencing
                </div>
              )}
              {layer.latest_inference_cutout_status ===
                STATUSES.IN_PROGRESS && (
                <div className={styles.statusOverlay}>
                  {OVERLAY_BY_SERVICE[service]}
                </div>
              )}
              {layer.is_processing_masks && (
                <div className={styles.statusOverlay}>
                  Processing...
                  <div className={styles.loader}>
                    <div className={styles.pipe} />
                    <div className={styles.dashes} />
                  </div>
                </div>
              )}
            </>
          )}
          {layerThumbUrl && (
            <img
              alt=""
              className={styles.thumbnailImage}
              src={layerThumbUrl}
              style={{
                objectFit:
                  service === SERVICES.ROTOSCOPING ||
                  service === SERVICES.SMART_VECTORS
                    ? "contain"
                    : "fill",
              }}
            />
          )}
          {!layerThumbUrl && (
            <AspectRatioIcon
              className={
                layersViewMode === ViewModes.Preview
                  ? styles.largeIcon
                  : styles.smallIcon
              }
            />
          )}
        </div>
      )}
      {!wrappedByHeader && (
        <div
          className={cx(
            styles.layerDetails,
            layersViewMode === ViewModes.List && styles.listLayerDetails,
          )}
        >
          {showRenameLayerModal && selectedLayer?.id === layer.id ? (
            <input
              type="text"
              className={styles.editLayerName}
              defaultValue={layer.name}
              ref={layerInputEl}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              onBlur={(e) => {
                if (e.relatedTarget?.classList.contains("MuiMenu-paper"))
                  return;
                const newName = e.currentTarget.value.trim();
                const newLayer = JSON.parse(JSON.stringify(layer));
                newLayer.name = newName;
                if (layerNameIsValid(newName)) {
                  renameLayer(layer.id, e.currentTarget.value)
                    .then(() => {
                      setLayers(
                        [
                          newLayer,
                          ...(layers?.filter((v) => v.id !== layer.id) || []),
                        ].sort((l1, l2) => l2.id - l1.id),
                      );
                      setCutoutInProgress(null);
                      setSelectedLayer(null);
                    })
                    .catch((error) => toast.error(error.data));
                }
                setShowRenameLayerModal?.(false);
              }}
            />
          ) : (
            <>
              {layersViewMode === ViewModes.List && (
                <>
                  {layer.latest_inference_cutout_status === STATUSES.FAILED && (
                    <div className={styles.listLayerStatus}>
                      Error occurred...
                    </div>
                  )}
                  {layer.latest_inference_cutout_status ===
                    STATUSES.IN_PROGRESS && (
                    <div className={styles.listLayerStatus}>
                      {OVERLAY_BY_SERVICE[service]}
                    </div>
                  )}
                  {layer.is_processing_masks && (
                    <div className={styles.listLayerStatus}>
                      Processing...
                      <div className={styles.listLoader}>
                        <div className={styles.listPipe} />
                        <div className={styles.listDashes} />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div
                className={cx(
                  styles.name,
                  service === SERVICES.RETIME && styles.italicName,
                )}
                id={`layer-name-${layer.id}`}
              >
                {layer.name}
              </div>
            </>
          )}
        </div>
      )}
      <Dialog
        open={!!failedLayer}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">Could not rename layer</h3>
            <button
              onClick={() => {
                setFailedLayer(null);
              }}
              type="button"
              className="closeButton"
            >
              <XMark />
            </button>
          </div>
          <div className={cx("modalText", styles.layerModalText)}>
            <p>
              The layer name you have chosen <span> {failedLayer}</span>
              &nbsp;is not supported unfortunately. Please try to rename the
              layer again using any combination of the supported characters
              provided below:
              <ul>
                <li>Letters (a-z, A-Z)</li>
                <li>Numbers (0-9)</li>
                <li>Whitespace (\s)</li>
                <li>Hyphen (-)</li>
                <li>Underscore (_)</li>
              </ul>
            </p>
          </div>
          <div className={cx("modalActions", styles.layerModalActions)}>
            <Button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                setFailedLayer(null);
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Layer;
